<template>
  <div>
    <div class="flex flex-col xl:flex-row pb-4 b-b">
      <div class="xl:w-55% p-x order-2 xl:order-1">
        <h1
          id="focus-target"
          class="mb-5 text-2xl font-medium leading-none focus-b"
        >
          <IconBase :icon="icon" size="1.6rem" class="relative top-1" />
          {{ htmlDecode(category.title) }}
        </h1>
        <RouterLink
          v-if="category.type === 'subject'"
          to="/campus/subjects"
          class="block text-primary underline text-sm mb-2 focus-a"
        >
          Change subject
        </RouterLink>
        <p v-html="category.description" />
        <Button
          v-if="category.linkout"
          text="Find out more"
          textcolor="primary"
          type="external"
          :iconright="true"
          icon="LinkIcon"
          iconsize="1rem"
          iconcolor="primary"
          :path="category.linkout"
          class="mt-5 underline"
        />
      </div>
      <div
        class="order-1 mb-4 flex-c h-48 xl:block xl:h-auto xl:w-45% xl:order-2 overflow-hidden"
      >
        <img :src="image.src" :alt="image.alt" class="w-full h-auto" />
      </div>
    </div>

    <div class="p-x py-4 b-b">
      <h2 class="text-xl font-medium" v-html="`Explore`" />
      <nav aria-label="Content" :class="{ 'order-1 mb-6': phone }">
        <ul class="content-list">
          <li
            v-for="item in categoryChildren"
            :key="item.id"
            class="content-list__item"
          >
            <RouterLink
              :to="path(item)"
              :event="item.type === 'location' ? '' : 'click'"
              class="relative flex p-2 text-primary border border-grey-2 bg-white focus-b"
              :class="[
                item.type === 'location' ? `map__marker-link--${item.slug}` : ''
              ]"
              :data-gtm-category-sidebar-item="{
                category: category.title,
                title: item.title,
                type: item.type
              }"
              @click.native="handleClick(item)"
              @focus.native="handleFocus($event, item)"
              @blur.native="handleBlur(item)"
              @keyup.native.enter="handleEnter(item)"
              @keyup.native.escape="handleBlur(item)"
            >
              <IconBase
                :icon="`${item.type}icon`"
                size="1.1rem"
                color="primary"
                class="mr-3 self-center"
              />
              {{ htmlDecode(item.title) }}
            </RouterLink>
          </li>
        </ul>
      </nav>
    </div>

    <div v-if="category.video" class="p-x pt-8 pb-4 b-b">
      <Video :post="category.video" textcolor="text-body" />
    </div>

    <div v-if="personaImage && personaIntro" class="persona my-6 xl:flex">
      <div class="mb-6 w-48">
        <div class="h-32 p-x xl:border-r-2">
          <div
            role="img"
            :alt="personaImage.alt"
            :style="`background-image: url(${personaImage.src})`"
            class="persona__avatar h-full bg-full shadow-lg"
            :class="[`bg-${categoryAccentColor}`]"
          />
        </div>
      </div>
      <div class="p-x xl:w-2/3">
        <div class="flex flex-col xl:justify-between xl:h-32">
          <blockquote class="text-2xl leading-tight">
            <p class="">"{{ htmlDecode(personaIntro) }}...</p>
          </blockquote>

          <Button
            v-if="category.persona.linkout"
            :text="category.persona.link_text"
            textcolor="primary"
            type="external"
            :iconright="true"
            icon="ArrowIcon"
            iconcolor="primary"
            :path="category.persona.linkout"
            class="underline"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import IconBase from '@/components/icons/IconBase.vue'
import Button from '@/components/buttons/Button.vue'
import Video from '@/components/Video.vue'

export default {
  name: 'Category',

  components: {
    IconBase,
    Button,
    Video
  },

  props: {
    category: {
      type: Object,
      default: () => {}
    }
  },

  computed: {
    ...mapState(['touch', 'mobile', 'phone', 'browser']),
    ...mapGetters([]),

    categoryChildren() {
      const mapped = this.category.children.map(item => {
        if (item.type === 'location') {
          const filtered = item.children.filter(
            o => o.parents.includes(this.category.id) || !o.parents
          )

          return {
            ...item,
            children: filtered
          }
        } else {
          return item
        }
      })

      return mapped.filter(item => {
        return item.type === 'location' ? item.children.length : item
      })
    },

    image() {
      if (this.category.image)
        return {
          src: this.category.image.sizes.medium,
          alt: this.category.image.alt
        }
      return ''
    },

    personaImage() {
      if (this.category.persona.image)
        return {
          src: this.category.persona.image.sizes.medium,
          alt: this.category.persona.image.alt
        }
      return ''
    },

    personaIntro() {
      if (this.category.persona.introduction) {
        return this.category.persona.introduction.slice(0, 80)
      }
      return ''
    },

    icon() {
      return this.category.type === 'subject'
        ? 'subjectsicon'
        : `${this.category.icon}icon`
    },

    categoryAccentColor() {
      switch (this.category.slug) {
        case 'accommodation':
          return 'highlight-2'
        case 'social':
          return 'highlight-3'
        case 'student-services':
          return 'highlight-4'
        default:
          return 'white'
      }
    }
  },

  methods: {
    path(item) {
      if (item.type === 'location')
        return {
          name: 'campus-panorama',
          params: {
            category_id: this.category.slug,
            media_id: item.slug,
            panorama_id: item?.children[0]?.slug
          }
        }
      return {
        name: 'campus-media',
        params: { category_id: this.category.slug, media_id: item.slug }
      }
    },

    handleFocus(e, item) {
      // Handles showing the map marker on focus enter press which leaflet fails with
      if (item.type === 'location') {
        const domNode = e.target
        const clickTarget = domNode.querySelector('.map__marker-link')
        if (clickTarget) {
          clickTarget.click()
        }
      }
    },

    handleBlur(item) {},

    handleEnter(item) {
      if (item.type === 'location') {
        this.$router.push(this.path(item))
      }
    },

    handleClick(item) {
      if (item.type === 'location' && this.mobile) {
        this.$router.push(this.path(item))
      }
    }
  }
}
</script>

<style>
.persona {
  position: relative;
  margin-left: 0.7rem;
}

.persona:before {
  content: '';
  position: absolute;
  height: 100%;
  width: 2px;
  @apply bg-body;
  left: 0.2rem;
  top: 0;
}

.persona__avatar {
  position: relative;
  margin-left: 0.75rem;
}

.persona__avatar:before {
  content: '';
  position: absolute;
  height: 80%;
  width: 0.75rem;
  top: 50%;
  left: -0.75rem;
  background-color: inherit;
  transform: translateY(-50%);
}

.content-list__item {
  margin-top: -1px;
}

@screen xl {
  .content-list {
    columns: 2;
  }

  .content-list__item {
    break-inside: avoid;
  }
}

@screen xl {
  .persona {
    margin-left: 0;
  }

  .persona:before {
    display: none;
  }
}
</style>
